import React, { CSSProperties, useEffect } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Box,
  Checkbox,
  IconButton,
} from "@chakra-ui/react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type Column,
  type Header,
  type Cell,
} from "@tanstack/react-table";
import { LuArrowDown, LuArrowDownUp, LuArrowUp, LuGripVertical } from "react-icons/lu";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export interface TableColumn {
  label: string;
  key: string;
  sortable?: boolean;
  render?: (value: unknown, row: Record<string, unknown>) => React.ReactNode;
}

interface GenericTableProps {
  data: Record<string, unknown>[];
  columns: TableColumn[];
  enableRowSelection?: boolean;
  enableColumnOrdering?: boolean;
  onRowSelectionChange?: (selectedRows: unknown) => void;
  size?: "sm" | "md" | "lg";
}

const SELECTION_COLUMN_WIDTH = "1px";

const DraggableTableHeader = ({
  header,
  getSortIcon,
  enableColumnOrdering,
}: {
  header: Header<Record<string, unknown>, unknown>;
  getSortIcon: (column: Column<Record<string, unknown>, unknown>) => React.ReactNode;
  enableColumnOrdering: boolean;
}) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id: header.column.id,
  });

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: "relative",
    transform: CSS.Translate.toString(transform),
    transition: "width transform 0.2s ease-in-out",
    whiteSpace: "nowrap",
    width: header.column.getSize(),
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <Th
      ref={setNodeRef}
      style={style}
      colSpan={header.colSpan}
      px={1}
      cursor={header.column.getCanSort() ? "pointer" : "default"}
      userSelect="none"
      onClick={header.column.getToggleSortingHandler()}
      _hover={header.column.getCanSort() ? { backgroundColor: "gray.50" } : {}}
    >
      <Box display="flex" alignItems="center" gap={1}>
        {enableColumnOrdering && (
          <IconButton
            aria-label="Drag to reorder"
            icon={<LuGripVertical />}
            size="xs"
            variant="ghost"
            cursor="grab"
            {...attributes}
            {...listeners}
            onClick={e => e.stopPropagation()} // Prevent sort when dragging
          />
        )}
        {flexRender(header.column.columnDef.header, header.getContext())}
        {getSortIcon(header.column)}
      </Box>
    </Th>
  );
};

const DragAlongCell = ({
  cell,
  isSelected,
}: {
  cell: Cell<Record<string, unknown>, unknown>;
  isSelected: boolean;
}) => {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  });

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: "relative",
    transform: CSS.Translate.toString(transform),
    transition: "width transform 0.2s ease-in-out",
    width: cell.column.getSize(),
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <Td ref={setNodeRef} style={style} px={1}>
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </Td>
  );
};

export function GenericTable({
  data,
  columns,
  enableRowSelection = false,
  enableColumnOrdering = true,
  onRowSelectionChange,
  size = "md",
}: GenericTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [rowSelection, setRowSelection] = React.useState({});

  const tableColumns: ColumnDef<Record<string, unknown>>[] = React.useMemo(() => {
    const cols = columns.map(col => ({
      id: col.key,
      header: col.label,
      accessorKey: col.key,
      enableSorting: col.sortable !== false,
      // @ts-ignore - row type is inferred from TanStack Table context
      cell: ({ getValue, row }) => (col.render ? col.render(getValue(), row.original) : getValue()),
    }));

    // Add selection column if enabled
    if (enableRowSelection) {
      cols.unshift({
        id: "select",
        header: "Select",
        accessorKey: "select",
        cell: () => null,
        enableSorting: false,
      });
    }

    return cols;
  }, [columns, enableRowSelection]);

  // Initialize column order from columns
  const [columnOrder, setColumnOrder] = React.useState<string[]>(() =>
    tableColumns.map(c => c.id!),
  );

  // Update column order when columns change
  useEffect(() => {
    setColumnOrder(tableColumns.map(c => c.id!));
  }, [tableColumns]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      rowSelection,
      columnOrder: enableColumnOrdering ? columnOrder : undefined,
    },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnOrderChange: enableColumnOrdering ? setColumnOrder : undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection,
  });

  useEffect(() => {
    if (onRowSelectionChange) {
      const selectedRows = table.getSelectedRowModel().rows.map(row => row.original);
      onRowSelectionChange(selectedRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);

  const getSortIcon = (column: Column<Record<string, unknown>, unknown>) => {
    if (!column.getCanSort()) {
      return null;
    }

    const sortDirection = column.getIsSorted();
    if (sortDirection === "asc") {
      return <LuArrowUp />;
    }
    if (sortDirection === "desc") {
      return <LuArrowDown />;
    }
    return <LuArrowDownUp />;
  };

  // Handle drag end for column reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder(currentOrder => {
        const oldIndex = currentOrder.indexOf(active.id as string);
        const newIndex = currentOrder.indexOf(over.id as string);
        return arrayMove(currentOrder, oldIndex, newIndex);
      });
    }
  };

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  // Render table content
  const renderTableContent = () => (
    <TableContainer>
      <Table variant="simple" size={size}>
        <Thead>
          {table.getHeaderGroups().map(headerGroup => (
            <Tr key={headerGroup.id}>
              {enableColumnOrdering ? (
                <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                  {headerGroup.headers.map(header => {
                    if (header.id === "select") {
                      return (
                        <Th
                          key={header.id}
                          px={1}
                          w={SELECTION_COLUMN_WIDTH}
                          position="sticky"
                          left={0}
                          zIndex={2}
                          bg="white"
                          _dark={{ bg: "gray.800" }}
                        >
                          <Checkbox
                            isChecked={table.getIsAllRowsSelected()}
                            isIndeterminate={table.getIsSomeRowsSelected()}
                            onChange={table.getToggleAllRowsSelectedHandler()}
                          />
                        </Th>
                      );
                    }
                    return (
                      <DraggableTableHeader
                        key={header.id}
                        header={header}
                        getSortIcon={getSortIcon}
                        enableColumnOrdering={enableColumnOrdering}
                      />
                    );
                  })}
                </SortableContext>
              ) : (
                headerGroup.headers.map(header => {
                  if (header.id === "select") {
                    return (
                      <Th
                        key={header.id}
                        px={1}
                        w={SELECTION_COLUMN_WIDTH}
                        position="sticky"
                        left={0}
                        zIndex={2}
                        bg="white"
                        _dark={{ bg: "gray.800" }}
                      >
                        <Checkbox
                          isChecked={table.getIsAllRowsSelected()}
                          isIndeterminate={table.getIsSomeRowsSelected()}
                          onChange={table.getToggleAllRowsSelectedHandler()}
                        />
                      </Th>
                    );
                  }
                  return (
                    <Th
                      key={header.id}
                      px={1}
                      cursor={header.column.getCanSort() ? "pointer" : "default"}
                      userSelect="none"
                      onClick={header.column.getToggleSortingHandler()}
                      _hover={header.column.getCanSort() ? { backgroundColor: "gray.50" } : {}}
                    >
                      <Box display="flex" alignItems="center">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {getSortIcon(header.column)}
                      </Box>
                    </Th>
                  );
                })
              )}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map(row => (
            <Tr
              key={row.id}
              backgroundColor={row.getIsSelected() ? "blue.50" : "transparent"}
              _hover={{ backgroundColor: row.getIsSelected() ? "blue.100" : "gray.50" }}
            >
              {enableColumnOrdering ? (
                <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                  {row.getVisibleCells().map(cell => {
                    if (cell.column.id === "select") {
                      return (
                        <Td
                          key={cell.id}
                          px={1}
                          w={SELECTION_COLUMN_WIDTH}
                          position="sticky"
                          left={0}
                          zIndex={1}
                          bg={row.getIsSelected() ? "blue.50" : "white"}
                          _dark={{
                            bg: row.getIsSelected() ? "blue.900" : "gray.800",
                          }}
                        >
                          <Checkbox
                            isChecked={row.getIsSelected()}
                            isIndeterminate={row.getIsSomeSelected()}
                            onChange={row.getToggleSelectedHandler()}
                          />
                        </Td>
                      );
                    }
                    return (
                      <DragAlongCell key={cell.id} cell={cell} isSelected={row.getIsSelected()} />
                    );
                  })}
                </SortableContext>
              ) : (
                row.getVisibleCells().map(cell => {
                  if (cell.column.id === "select") {
                    return (
                      <Td
                        key={cell.id}
                        px={1}
                        w={SELECTION_COLUMN_WIDTH}
                        position="sticky"
                        left={0}
                        zIndex={1}
                        bg={row.getIsSelected() ? "blue.50" : "white"}
                        _dark={{
                          bg: row.getIsSelected() ? "blue.900" : "gray.800",
                        }}
                      >
                        <Checkbox
                          isChecked={row.getIsSelected()}
                          isIndeterminate={row.getIsSomeSelected()}
                          onChange={row.getToggleSelectedHandler()}
                        />
                      </Td>
                    );
                  }
                  return (
                    <Td key={cell.id} px={1}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  );
                })
              )}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );

  // Wrap with DndContext if column ordering is enabled
  if (enableColumnOrdering) {
    return (
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        {renderTableContent()}
      </DndContext>
    );
  }

  return renderTableContent();
}

export default GenericTable;
